/**
 * Client for Playwright E2E health + kickoff (pro-api /e2e/*).
 */

import { authenticatedProApiFetch } from "./pro-api.client";

export type E2eSpecResult = {
  title: string;
  file: string;
  project: string;
  status: "passed" | "failed" | "skipped" | "timedOut" | "interrupted";
  durationMs: number;
  error?: string;
};

export type E2eRunProjectSegment = {
  name: string;
  durationMs: number;
  status?: "pending" | "running" | "passed" | "failed" | "skipped";
  totalSpecs?: number;
  completedSpecs?: number;
  currentIndex?: number;
  currentSpec?: string;
};

export type E2eRunSegments = {
  awsMs?: number;
  playwrightMs?: number;
  publishMs?: number;
  projects?: E2eRunProjectSegment[];
};

export type E2eHealthReport = {
  environment: string;
  generatedAt: string | null;
  conclusion: "passed" | "failed" | "cancelled" | "unknown";
  totals: { passed: number; failed: number; skipped: number; flaky?: number };
  /** Total wall clock kickoff → completion when available. */
  durationMs: number;
  /** Playwright suite time only. */
  playwrightDurationMs?: number;
  segments?: E2eRunSegments;
  github: { runId: number; runUrl: string; sha: string; branch: string };
  specs: E2eSpecResult[];
  empty: boolean;
  /** fixture = local dockerize mock; s3 = live CI publish. */
  source?: "fixture" | "s3";
  error?: string;
};

export type E2ePlaywrightProject = "smoke" | "authenticated" | "onboarding";

export type E2eRunStatus =
  | "queued"
  | "starting"
  | "running"
  | "succeeded"
  | "failed"
  | "stopped"
  | "timed_out"
  | "fault";

export type E2eRun = {
  runId: string;
  environment: string;
  status: E2eRunStatus;
  pipelineExecutionId?: string | null;
  codeBuildId?: string | null;
  projects?: string[];
  message?: string;
  phase?: string;
  phaseLabel?: string;
  stepLabel?: string;
  currentProject?: string;
  currentSpec?: string;
  /** 0–1 estimate for Preparing Runner (pre-Playwright). */
  prepareProgress?: number;
  durationMs?: number;
  segments?: E2eRunSegments;
  startedAt?: string;
  finishedAt?: string;
};

export type StartE2eRunResponse = {
  runId: string;
  pipelineExecutionId?: string | null;
  codeBuildId: string | null;
  environment: string;
  status: E2eRunStatus;
  projects: string[];
  message?: string;
  phaseLabel?: string;
  stepLabel?: string;
  startedAt?: string;
};

export async function fetchE2eHealth(
  accessToken: string,
  options?: { environment?: string },
): Promise<E2eHealthReport> {
  const env = options?.environment?.trim() || "dev";
  const qs = `?environment=${encodeURIComponent(env)}`;
  return authenticatedProApiFetch<E2eHealthReport>(`/e2e/health${qs}`, accessToken);
}

export async function startE2eRun(
  accessToken: string,
  body?: { environment?: string; projects?: E2ePlaywrightProject[] },
): Promise<StartE2eRunResponse> {
  return authenticatedProApiFetch<StartE2eRunResponse>("/e2e/runs", accessToken, {
    method: "POST",
    body: JSON.stringify({
      environment: body?.environment ?? "dev",
      ...(body?.projects?.length ? { projects: body.projects } : {}),
    }),
  });
}

export async function fetchE2eRun(
  accessToken: string,
  runId: string,
): Promise<E2eRun> {
  return authenticatedProApiFetch<E2eRun>(
    `/e2e/runs/${encodeURIComponent(runId)}`,
    accessToken,
  );
}

export async function stopE2eRun(
  accessToken: string,
  runId: string,
): Promise<E2eRun> {
  return authenticatedProApiFetch<E2eRun>(
    `/e2e/runs/${encodeURIComponent(runId)}/stop`,
    accessToken,
    { method: "POST", body: "{}" },
  );
}
